import {SurveySlug} from './types';

// Survey ids arrive as query params, named after the 2024 dashboard page configuration.
export interface LuogoSurveys {
    iniziale: number;
    aggiornamento: number;
    iniziativeIniziale: number;
    iniziativeAggiornamento: number;
    iniziativeFinale: number;
    fruitori: number;
    partecipativi: number;
}

// Guideline labels (A.11, B.28, …) are not SurveyJS names; these were resolved by question title.

// A.14 "Quali tipologie di lavori sono previste?" — explicit keys, since `count` matches by prefix.
const TIPOLOGIE_LAVORI_KEYS = [0, 1, 2, 3, 4].map((i) => `domanda4_${i}`);

// A.2 fruitori: explicit keys, because the prefix `question2` would also swallow `question20`.
export const ASPETTI_SODDISFACENTI_KEYS = [0, 1, 2, 3, 4, 5].map((i) => `question2_${i}`);

// Both encodings are matched: the survey now declares text values, older submissions carry "Item N".
const PHASES = [
    {label: 'Analisi dei bisogni', values: ['Item 1', 'Analisi dei bisogni']},
    {label: 'Scelta/rimodulazione funzioni d’uso', values: ['Item 2', 'Scelta/rimodulazione delle funzioni d’uso']},
    {label: 'Definizione usi temporanei', values: ['Item 3', 'Definizione di usi transitori del luogo durante la realizzazione delle opere']},
    {label: 'Catalogo/palinsesto attività', values: ['Item 4', 'Definizione del catalogo/palinsesto delle attività']},
    {label: 'Governance/gestione', values: ['Item 5', 'Definizione del modello di governance/gestione']},
    {label: 'Comunicazione/promozione', values: ['Item 6', 'Identificazione di strategie di comunicazione/promozione']},
    {label: 'Monitoraggio e valutazione', values: ['Item 7', 'Monitoraggio e valutazione']},
    {label: 'Altro', values: ['Item 8', 'Altro (specificare)']},
];

const ACTIVITIES = [
    {label: 'Sondaggi/raccolta opinioni', values: ['Item 1', 'Sondaggi/raccolta opinioni']},
    {label: 'Singoli incontri aperti', values: ['Item 2', 'Singoli incontri aperti alla cittadinanza']},
    {label: 'Serie incontri/workshop/seminari', values: ['Item 3', 'Serie di incontri/workshop/seminari rivolti a gruppi di cittadini selezionati (specificare il target)']},
    {label: 'Altre attività', values: ['Item 4', 'Altre attività (specificare)']},
];

// Rows of the age-target matrix (Domanda2) on the schede aggiornamento and finale iniziative.
export const AGE_GROUPS = [
    'Infanti (0-3 anni)',
    'Piccoli (4-6)',
    'Bambini (6-10)',
    'Pre-adolescenti (11-13)',
    'Adolescenti (14-18)',
    'Giovani (19-34)',
    'Adulti (35-64)',
    'Anziani (≥65)',
];

export const PHASE_LABELS = PHASES.map((phase) => phase.label);
export const ACTIVITY_LABELS = ACTIVITIES.map((activity) => activity.label);

export const TOOLTIPS = {
    superficie:
        'Il box mostra la percentuale di superficie oggetto di rigenerazione per il vostro progetto, a confronto con la media degli altri progetti. Il valore è ottenuto come rapporto tra la superficie del luogo che il progetto prevede di rigenerare e la superficie complessiva del luogo.',
    complessita:
        'Il box mostra la varietà delle tipologie di lavori previsti nell’ambito del vostro intervento, a confronto con la media dello stesso valore in tutti i progetti. Il valore medio è calcolato come il rapporto tra la somma totale delle tipologie diverse di lavori previsti in tutti gli interventi di rigenerazione e il numero totale di progetti.',
    avanzamento:
        'Il box mostra la percentuale di avanzamento dei lavori di riqualificazione del vostro progetto, a confronto con la media calcolata su tutti i progetti. Il valore medio è ottenuto dividendo la somma delle percentuali di avanzamento rilevate per ciascun progetto per il numero totale di progetti.',
    partecipazione:
        'Il box mostra il numero di cittadini coinvolti nelle attività partecipative relative al vostro progetto, a confronto con la media calcolata su tutti i progetti. Il valore medio è ottenuto dividendo la somma dei cittadini coinvolti in tutti i progetti per il numero totale di progetti che hanno attivato attività partecipative.',
    fasi:
        'Il grafico mostra la percentuale di partecipanti ai processi partecipativi per fase di coinvolgimento indicata, a confronto con la media per tutti i progetti. La percentuale media viene calcolata, per ciascuna fase indicata, sommando le percentuali calcolate per i singoli luoghi e dividendo il risultato per il numero totale di luoghi.\n\nPoiché ciascun rispondente può selezionare più fasi, la somma delle percentuali può essere superiore al 100%.',
    attivita:
        'Il grafico mostra la percentuale di partecipanti per tipologia di attività partecipativa indicata, a confronto con la media per tutte le attività partecipative indicate in tutti i progetti. La percentuale media viene calcolata, per ciascuna tipologia di attività partecipativa indicata, sommando le percentuali calcolate per i singoli luoghi e dividendo il risultato per il numero totale di luoghi.\n\nPoiché ciascun rispondente può selezionare più attività, la somma delle percentuali può essere superiore al 100%.',
    apertura:
        'Il box mostra il numero di giornate di apertura del luogo negli ultimi 12 mesi, a confronto con la media calcolata su tutti i progetti. Il valore medio è ottenuto dividendo la somma del numero di giorni di apertura in tutti i progetti per il numero totale di progetti.',
    fruitori:
        'Il box mostra la percentuale di fruitori effettivamente raggiunti rispetto al numero di fruitori attesi per l’insieme delle iniziative realizzate nel luogo, a confronto con la media dello stesso valore calcolata per tutti i luoghi. La percentuale media è ottenuta sommando le percentuali calcolate per ciascun luogo e dividendo il risultato per il numero totale di luoghi.',
    target:
        'Il grafico mostra la percentuale di iniziative del vostro progetto che si rivolgono a ciascun target di età, a confronto con la percentuale complessiva calcolata considerando tutte le iniziative realizzate in tutti i luoghi. La percentuale complessiva è calcolata, per ciascun target di età, dividendo il numero totale di iniziative rivolte a quella fascia per il numero complessivo di iniziative realizzate in tutti i luoghi.\n\nPoiché una stessa iniziativa può rivolgersi a più target di età, la somma delle percentuali può essere superiore al 100%.',
    frequenza:
        'Il grafico mostra, per tutte le iniziative realizzate nell’ambito del progetto, la percentuale di fruitori che ha visitato il luogo due o più volte nell’ultimo mese, a confronto con la media dello stesso valore per tutte le iniziative realizzate nell’ambito di tutti i progetti. Il valore medio viene calcolato sommando le percentuali rilevate per ogni progetto e dividendo il totale per il numero totale di luoghi.',
    ritorno:
        'Il grafico mostra, per tutte le iniziative realizzate nell’ambito del progetto, la percentuale di partecipanti che ha espresso l’intenzione di tornare, a confronto con la media dello stesso valore per tutte le iniziative realizzate nell’ambito di tutti i progetti. Il valore medio viene calcolato sommando le percentuali rilevate per ogni progetto e dividendo il totale per il numero totale di luoghi.',
    soddisfazione:
        'Il box mostra il numero medio di aspetti soddisfacenti indicati dai fruitori - possibilità di visitare uno spazio finora non in uso/aperto al pubblico; qualità dell’offerta culturale; affinità rispetto ai propri interessi/gusti; accessibilità del luogo di svolgimento; novità dell’iniziativa; altri aspetti - per ciascuna iniziativa, a confronto con la media dello stesso valore per tutte le iniziative realizzate nell’ambito di tutti i progetti. Il valore di confronto viene calcolato sommando il numero medio di aspetti soddisfacenti, calcolato per ciascuna iniziativa, e dividendo il risultato per il numero complessivo di iniziative.',
};

// Indicator 1.1 — A.11 ÷ A.6 of the scheda iniziale.
const buildSuperficieSlug = (surveys: LuogoSurveys): SurveySlug => ({
    key: 'superficie',
    numerator: {survey_id: surveys.iniziale, question: 'question14'},
    denominator: {survey_id: surveys.iniziale, question: 'question5'},
    operation: 'sum',
    perUoa: true,
    perUoaLatest: true,
});

// Indicator 1.2 — how many kinds of work the intervention covers (A.14).
const buildComplessitaSlug = (surveys: LuogoSurveys): SurveySlug => ({
    key: 'complessita',
    numerator: {survey_id: surveys.iniziale, question: TIPOLOGIE_LAVORI_KEYS},
    operation: 'count',
    perUoa: true,
    perUoaLatest: true,
});

// Indicator 1.3 — A.4 of the latest scheda aggiornamento, falling back to A.18 of the iniziale.
const buildAvanzamentoSlug = (surveys: LuogoSurveys): SurveySlug => ({
    key: 'avanzamento',
    numerator: {survey_id: surveys.aggiornamento, question: 'question22'},
    operation: 'sum',
    perUoa: true,
    perUoaLatest: true,
    fallbacks: [{operation: 'sum', numerator: {survey_id: surveys.iniziale, question: 'question22'}}],
});

// Indicator 1.4 — the filter makes the average divide by the luoghi that answered "Sì" to B.8/B.25.
const buildPartecipazioneSlug = (surveys: LuogoSurveys): SurveySlug => ({
    key: 'partecipazione',
    numerator: {survey_id: surveys.aggiornamento, question: 'question28'},
    operation: 'sum',
    filter: {question: 'question25', value: 'Item 1'},
    perUoa: true,
    perUoaLatest: true,
    fallbacks: [{operation: 'sum', numerator: {survey_id: surveys.iniziale, question: 'question30'}}],
});

// Indicators 1.5/1.6 — no latest-per-uoa: every respondent is a separate submission.
const buildChoiceShareSlugs = (
    surveyId: number,
    question: string,
    prefix: string,
    choices: {values: string[]}[],
): SurveySlug[] =>
    choices.map((choice, i) => ({
        key: `${prefix}_${i + 1}`,
        numerator: {survey_id: surveyId, question, value: choice.values},
        operation: 'match',
        perUoa: true,
    }));

// Indicator 1.7 — A.7a, deliberately no fallback: without an update sheet it is non calcolabile.
const buildAperturaSlug = (surveys: LuogoSurveys): SurveySlug => ({
    key: 'apertura',
    numerator: {survey_id: surveys.aggiornamento, question: 'domanda4'},
    operation: 'sum',
    perUoa: true,
    perUoaLatest: true,
});

// Indicator 1.8 — expected uses A.4a2 only per the 24.08 guidelines; A.4b1 is still open with the client.
const buildFruitoriSlug = (surveys: LuogoSurveys): SurveySlug => ({
    key: 'fruitori',
    numerator: {survey_id: surveys.iniziativeFinale, question: 'question6'},
    denominator: {survey_id: surveys.iniziativeIniziale, question: 'question6'},
    operation: 'sum',
    aggregateChildren: true,
    perUoaLatest: true,
    fallbacks: [{
        operation: 'sum',
        numerator: {survey_id: surveys.iniziativeAggiornamento, question: 'question6'},
        denominator: {survey_id: surveys.iniziativeIniziale, question: 'question6'},
    }],
});

// Indicator 1.9 — no denominator, so the fallback survey is recognised as the same measure.
const buildTargetSlugs = (surveys: LuogoSurveys): SurveySlug[] =>
    AGE_GROUPS.map((_, i) => ({
        key: `target_${i + 1}`,
        numerator: {survey_id: surveys.iniziativeFinale, question: `domanda2_row_${i + 1}_column_1`},
        operation: 'sum' as const,
        aggregateChildren: true,
        perUoaLatest: true,
        positiveRatio: true,
        fallbacks: [{
            operation: 'sum' as const,
            numerator: {survey_id: surveys.iniziativeAggiornamento, question: `domanda2_row_${i + 1}_column_1`},
        }],
    }));

// Indicators 1.10/1.11 — A.3 and A.4 of the questionario fruitori, pooled over the luogo's iniziative.
const buildFruizioneSlugs = (surveys: LuogoSurveys): SurveySlug[] => [
    {
        key: 'frequenza',
        numerator: {survey_id: surveys.fruitori, question: 'question3', value: ['Item 3', 'Item 4']},
        operation: 'match',
        aggregateChildren: true,
    },
    {
        key: 'ritorno',
        numerator: {survey_id: surveys.fruitori, question: 'question4', value: ['Item 3', 'Item 4']},
        operation: 'match',
        aggregateChildren: true,
    },
];

export const buildLuogoSlugs = (surveys: LuogoSurveys): SurveySlug[] => [
    buildSuperficieSlug(surveys),
    buildComplessitaSlug(surveys),
    buildAvanzamentoSlug(surveys),
    buildPartecipazioneSlug(surveys),
    ...buildChoiceShareSlugs(surveys.partecipativi, 'question3', 'fase', PHASES),
    ...buildChoiceShareSlugs(surveys.partecipativi, 'question1', 'attivita', ACTIVITIES),
    buildAperturaSlug(surveys),
    buildFruitoriSlug(surveys),
    ...buildTargetSlugs(surveys),
    ...buildFruizioneSlugs(surveys),
];

// Indicator 2.1 — average satisfying aspects per respondent, against the mean of all iniziative.
export const buildIniziativaSlugs = (fruitoriSurveyId: number): SurveySlug[] => [
    {
        key: 'soddisfazione',
        numerator: {survey_id: fruitoriSurveyId, question: ASPETTI_SODDISFACENTI_KEYS},
        operation: 'count',
        perUoa: true,
    },
];
